/**
 * How the diet plan page shows a saved plan: which meal comes next today, each meal's kind and main foods, and the
 * plan's words as people read them. Display only: the saved plan is never changed here.
 */

/** "150g" -> "150 g", "1 colher de chá (5g)" -> "1 colher de chá (5 g)": the AI writes quantities without the space. */
export const formatQuantity = (quantity) => String(quantity ?? '').replace(/(\d)\s?(g|ml|kg)\b/gi, '$1 $2');

/**
 * "Café da Manhã" -> "Café da manhã". The AI copies the prompt's Title Case into the plans it writes, and pt-BR
 * doesn't capitalize every word.
 */
export const sentenceCase = (text) => {
  const value = String(text ?? '');
  return value.charAt(0) + value.slice(1).toLowerCase();
};

/** The meal to eat next: the first one with nothing in the tracker yet, or -1 once they're all logged. */
export const nextMealIndex = (meals, isLogged) => (meals || []).findIndex((_, index) => !isLogged(index));

/** What kind of meal it is, from its name (the AI names them like the prompt's meal types): it picks the icon. */
export const mealKind = (meal) => {
  if (meal?.isFreeMeal) return 'free';
  const name = String(meal?.name ?? '').toLowerCase();
  if (/caf[eé]|breakfast/.test(name)) return 'breakfast';
  if (/jantar|ceia|dinner|supper/.test(name)) return 'dinner';
  if (/lanche|snack/.test(name)) return 'snack';
  return 'meal';
};

/**
 * The meal's main foods, the ones with the most calories, in the order the meal lists them: "Arroz branco" and
 * "feijão carioca". Only the first keeps its capital in pt, so the names read as one line.
 */
export const mainFoods = (foods, { count = 2, lowerRest = false } = {}) => {
  const list = foods || [];
  const top = new Set([...list].sort((a, b) => (b.calories || 0) - (a.calories || 0)).slice(0, count));
  return list
    .filter((food) => top.has(food))
    .map((food, index) => (index > 0 && lowerRest ? food.name.charAt(0).toLowerCase() + food.name.slice(1) : food.name));
};

const MACROS = [
  { key: 'protein', perGram: 4 },
  { key: 'carbs', perGram: 4 },
  { key: 'fat', perGram: 9 },
];

/** Protein, carbs and fat: grams, and each one's share of their calories (the macro rings' arc). */
export const macroShares = (totals) => {
  const kcal = MACROS.reduce((sum, { key, perGram }) => sum + (totals?.[key] || 0) * perGram, 0) || 1;
  return MACROS.map(({ key, perGram }) => {
    const grams = totals?.[key] || 0;
    const share = (grams * perGram) / kcal;
    return { key, grams, share, pct: Math.round(share * 100) };
  });
};
